import { useCallback, useEffect, useState } from "react";
import { DEFAULT_EVENT_TEXTS, EVENT_TEXT_KEYS, type EventTextKey, type EventTexts } from "@shared";
import * as api from "./adminApi";
import I18nFields from "./I18nFields";

interface Props {
  eventId: string;
}

/** Libellés du formulaire, dans l'ordre où le jeune les lira. */
const LABELS: Record<EventTextKey, string> = {
  app_title: "Titre du site (toutes les pages)",
  app_subtitle: "Sous-titre de la page de connexion",
  welcome_title: "Titre du mot de bienvenue",
  welcome_body: "Mot de bienvenue",
  mission1_title: "Titre de l'expédition 1",
  mission1_body: "Présentation de l'expédition 1",
  mission2_title: "Titre de l'expédition 2",
  mission2_body: "Présentation de l'expédition 2",
  contest_title: "Titre du concours",
  contest_body: "Présentation du concours",
};

/** Ces textes sont longs : deux zones de texte plutôt que deux champs. */
const MULTILINE = new Set<EventTextKey>([
  "welcome_body",
  "mission1_body",
  "mission2_body",
  "contest_body",
]);

/**
 * Textes affichés au jeune. Le mot de bienvenue apparaît à sa première
 * connexion, et reste relisable ensuite depuis l'accueil.
 */
export default function TextsPanel({ eventId }: Props) {
  const [texts, setTexts] = useState<EventTexts | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setTexts(await api.getEventTexts(eventId));
    } catch {
      setError("Impossible de charger les textes.");
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (texts === null) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      setTexts(await api.saveEventTexts(eventId, texts));
      setSaved(true);
    } catch {
      setError("Enregistrement impossible. Chaque texte doit avoir une version française.");
    } finally {
      setBusy(false);
    }
  }

  if (texts === null) {
    return (
      <section className="panel">
        <h2 className="panel__title">Textes</h2>
        <p className="panel__hint">{error ?? "Chargement…"}</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Textes affichés au jeune</h2>
      <p className="panel__hint">
        Le mot de bienvenue s'affiche à la première connexion, avec le bouton « Mission acceptée ».
        Il reste consultable ensuite depuis l'accueil. Les sauts de ligne sont conservés.
      </p>
      <p className="panel__hint">
        Les deux textes du concours n'apparaissent que si le concours est activé sur la
        mission, et une fois les deux expéditions terminées. Le{" "}
        <strong>consentement du concours</strong>, lui, ne se modifie pas ici : il vit dans{" "}
        <code>content/consent.fr.md</code> et <code>content/consent.en.md</code>, parce qu'un texte
        de consentement se relit avant d'être publié et que son historique doit rester dans le
        dépôt.
      </p>

      {error !== null && <p className="form__error">{error}</p>}
      {saved && <p className="form__saved">Textes enregistrés.</p>}

      <form className="form" onSubmit={(e) => void submit(e)}>
        {EVENT_TEXT_KEYS.map((key) => {
          // Un texte enregistré fige la valeur par défaut : une amélioration
          // apportée plus tard ne le rattrape jamais. D'où cette remise à zéro
          // TEXTE PAR TEXTE — l'ancienne, globale, obligeait à sacrifier ses
          // sept autres personnalisations pour en récupérer une seule.
          const modifie = JSON.stringify(texts[key]) !== JSON.stringify(DEFAULT_EVENT_TEXTS[key]);
          return (
            <div className="texts__field" key={key}>
              <I18nFields
                id={`text-${key}`}
                label={LABELS[key]}
                value={texts[key]}
                multiline={MULTILINE.has(key)}
                onChange={(next) => {
                  setTexts({ ...texts, [key]: next });
                  setSaved(false);
                }}
              />
              {modifie && (
                <button
                  type="button"
                  className="button button--small texts__reset"
                  title="Remettre le texte d'origine, sans toucher aux autres"
                  onClick={() => {
                    setTexts({ ...texts, [key]: DEFAULT_EVENT_TEXTS[key] });
                    setSaved(false);
                  }}
                >
                  ↺ Texte d'origine
                </button>
              )}
            </div>
          );
        })}

        <div className="button-row">
          <button type="submit" className="button button--primary" disabled={busy}>
            Enregistrer
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              // Revenir aux textes d'origine : pratique après une mauvaise
              // manipulation. Rien n'est enregistré tant qu'on ne valide pas.
              setTexts({ ...DEFAULT_EVENT_TEXTS });
              setSaved(false);
            }}
          >
            Rétablir les textes par défaut
          </button>
        </div>
      </form>
    </section>
  );
}
